import { Box, Grid, Typography } from '@material-ui/core';
import { makeStyles, Theme } from '@material-ui/core/styles';
import {
  Button,
  createAutocompleteAddressField,
  createCheckboxField,
  createNumberFormatField,
  createPhoneField,
  createTextField,
} from '@roc/ui';
import { parseGoogleMapsAddress } from '@roc/feature-utils';
import { observer } from 'mobx-react';
import { WireInstructionsLoanDetailsCard } from './wireInstructionsLoanDetailsCard';
import { WireInstructionsStore } from '../stores/wireInstructionsStore';

const useStyles = makeStyles((theme: Theme) => ({
  button: {
    fontWeight: 'normal',
    padding: theme.spacing(2, 8),
  },
  sectionTitle: {
    fontWeight: 600,
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(1),
  },
  sectionSubtitle: {
    marginBottom: theme.spacing(1),
  },
}));

interface Props {
  store: WireInstructionsStore;
  onContinue: () => void;
}

export const BankInfoStep = observer(({ store, onContinue }: Props) => {
  const classes = useStyles();

  const handleAddressChange = (event: any) => {
    const target = event?.target || {};
    store.onFieldChange('address', target.value);

    const components = target.fullInfo?.address_components;
    if (components) {
      const parsed = parseGoogleMapsAddress(components);
      store.onFieldChange(
        'streetName',
        [parsed.street_number, parsed.street].filter(Boolean).join(' ')
      );
      store.onFieldChange('city', parsed.city);
      store.onFieldChange('zipCode', parsed.zip);
      store.onFieldChange('state', parsed.state);
    }
  };

  const handleContinue = () => {
    if (store.validateBankInfoStep()) {
      onContinue();
    }
  };

  return (
    <Grid container spacing={4}>
      <Grid item xs={12} md={6}>
        <Grid container spacing={2}>
          {/* Title Company Wire Instructions */}
          <Grid item xs={12}>
            <Typography className={classes.sectionTitle}>
              Title Company Wire Instructions
            </Typography>
          </Grid>
          <Grid item xs={12}>
            {createTextField({
              store,
              testId: 'entityName',
              fieldName: 'entityName',
              label: 'Title Company Legal Name',
            })}
          </Grid>
          <Grid item xs={12}>
            {createAutocompleteAddressField({
              store,
              testId: 'address',
              fieldName: 'address',
              label: 'Title Company Address',
              onChange: handleAddressChange,
            })}
          </Grid>
          <Grid item xs={12} sm={6}>
            {createTextField({
              store,
              testId: 'authorizedContactName',
              fieldName: 'authorizedContactName',
              label: 'Authorized Contact Name',
            })}
          </Grid>
          <Grid item xs={12} sm={6}>
            {createTextField({
              store,
              testId: 'authorizedContactTitle',
              fieldName: 'authorizedContactTitle',
              label: 'Authorized Contact Title',
            })}
          </Grid>
          <Grid item xs={12}>
            {createPhoneField({
              store,
              testId: 'directPhone',
              fieldName: 'directPhone',
              label: 'Direct Phone (for verification)',
            })}
          </Grid>
          <Grid item xs={12}>
            {createTextField({
              store,
              testId: 'emailAddress',
              fieldName: 'emailAddress',
              label: 'Email Address',
            })}
          </Grid>
          <Grid item xs={12}>
            {createTextField({
              store,
              testId: 'titleLicenseNumber',
              fieldName: 'titleLicenseNumber',
              label: 'Title License / Escrow Number',
            })}
          </Grid>

          {/* Beneficiary Bank Information */}
          <Grid item xs={12}>
            <Typography className={classes.sectionTitle}>
              Beneficiary Bank Information
            </Typography>
          </Grid>
          <Grid item xs={12}>
            {createTextField({
              store,
              testId: 'bankName',
              fieldName: 'bankName',
              label: 'Receiving / Beneficiary Bank Name',
            })}
          </Grid>
          <Grid item xs={12}>
            {createNumberFormatField({
              store,
              testId: 'routingNumber',
              fieldName: 'routingNumber',
              label: 'ABA / Routing Number (9 digits)',
              format: '#########',
            })}
          </Grid>
          <Grid item xs={12}>
            {createTextField({
              store,
              testId: 'accountName',
              fieldName: 'accountName',
              label: 'Account Name',
            })}
          </Grid>
          <Grid item xs={12}>
            {createNumberFormatField({
              store,
              testId: 'accountNumber',
              fieldName: 'accountNumber',
              label: 'Account Number',
            })}
          </Grid>
          <Grid item xs={12}>
            {createTextField({
              store,
              testId: 'referenceId',
              fieldName: 'referenceId',
              label: 'Reference / Memo Line',
            })}
          </Grid>

          {/* For International Wires Only (if applicable) */}
          <Grid item xs={12}>
            <Typography className={classes.sectionTitle}>
              For International Wires Only (if applicable)
            </Typography>
          </Grid>
          <Grid item xs={12}>
            {createTextField({
              store,
              testId: 'swiftCode',
              fieldName: 'swiftCode',
              label: 'SWIFT / BIC Code',
            })}
          </Grid>
          <Grid item xs={12}>
            {createTextField({
              store,
              testId: 'intermediaryBankName',
              fieldName: 'intermediaryBankName',
              label: 'Intermediary / Correspondent Bank Name',
            })}
          </Grid>
          <Grid item xs={12}>
            {createNumberFormatField({
              store,
              testId: 'intermediaryRoutingNumber',
              fieldName: 'intermediaryRoutingNumber',
              label: 'Intermediary ABA / Routing Number',
              format: '#########',
            })}
          </Grid>

          <Grid item xs={12}>
            {createCheckboxField({
              store,
              fieldName: 'escrowAccountCompliance',
              label:
                'I confirm that the above escrow/trust account is maintained in compliance with applicable state law and that funds deposited therein will not be commingled with general operating funds of the Title Company.',
            })}
          </Grid>
        </Grid>
      </Grid>
      <Grid item xs={12} md={6}>
        <WireInstructionsLoanDetailsCard loanData={store.loanData} />
      </Grid>
      <Grid item xs={12}>
        <Box display="flex" justifyContent="center">
          <Button
            testId="continue"
            variant="contained"
            color="primary"
            className={classes.button}
            onClick={handleContinue}
          >
            CONTINUE
          </Button>
        </Box>
      </Grid>
    </Grid>
  );
});
